// NIS2 onboarding page under NIS2 module navigation

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  getLocalizedOnboardingConfig,
  OnboardingLayout,
  useCompleteOnboarding,
  useCreateOnboarding,
  useOnboardingSession,
  useOnboardingSessions,
  useOnboardingStore,
  useUpdateOnboarding,
} from '@/features/ai/onboarding';
import type { OnboardingModule } from '@/features/ai/onboarding';
import { useCreateJurisdiction } from '@/features/nis2/api/jurisdictions';
import { NIS2JurisdictionSelect } from '@/features/nis2/components/NIS2JurisdictionSelect';
import { EU_TRANSPOSITIONS } from '@/features/nis2/constants/transpositions';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/nis2/onboarding')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.nis2Onboarding'),
  }),
  component: NIS2OnboardingPage,
  validateSearch: (search: Record<string, unknown>) => ({
    sessionId: search.sessionId as string | undefined,
  }),
});

function NIS2OnboardingPage() {
  const { i18n, t } = useTranslation('nis2');
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = Route.useSearch();
  const module: OnboardingModule = 'nis2';

  const { organizations } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const orgId = activeOrg?.id ?? '';

  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>([]);
  const createJurisdictionMutation = useCreateJurisdiction(orgId);

  const activeSessionId = useOnboardingStore.use.activeSessionId();
  const setActiveSession = useOnboardingStore.use.setActiveSession();
  const setCurrentModule = useOnboardingStore.use.setCurrentModule();
  const setLocalCurrentStep = useOnboardingStore.use.setLocalCurrentStep();
  const reset = useOnboardingStore.use.reset();
  const onboardingConfig = getLocalizedOnboardingConfig(module, t);
  const fieldLabels = new Map(
    onboardingConfig.steps.flatMap((step) =>
      step.fields.map((field) => [field.name, field.label] as const)
    )
  );

  const { data: existingSessions, isLoading: isLoadingSessions } = useOnboardingSessions({
    module,
    status: 'in_progress',
    enabled: !urlSessionId,
  });
  const {
    data: completedSessions,
    isLoading: isLoadingCompletedSessions,
    isFetching: isFetchingCompletedSessions,
  } = useOnboardingSessions({
    module,
    status: 'completed',
    enabled: !urlSessionId,
  });

  const sessionIdToUse = urlSessionId ?? existingSessions?.items[0]?.id ?? activeSessionId;
  const {
    data: session,
    isLoading: isLoadingSession,
    error: sessionError,
  } = useOnboardingSession(sessionIdToUse);

  const { mutate: createSession, isPending: isCreating } = useCreateOnboarding({
    onSuccess: (newSession) => {
      setActiveSession(newSession.id);
      setCurrentModule(newSession.module);
      setLocalCurrentStep(newSession.currentStep);
      void navigate({
        to: '/nis2/onboarding',
        search: { sessionId: newSession.id },
        replace: true,
      });
    },
    onError: (error) => {
      if (error instanceof ApiClientError && error.status === 409) {
        const existingId = (error as ApiClientError & { existingSessionId?: string })
          .existingSessionId;
        if (existingId) {
          setActiveSession(existingId);
          void navigate({
            to: '/nis2/onboarding',
            search: { sessionId: existingId },
            replace: true,
          });
          return;
        }
      }
      toast.error(t('nis2:toast.failedToStartOnboarding'), { description: error.message });
    },
  });

  const { mutate: updateSession, isPending: isUpdating } = useUpdateOnboarding({
    onError: (error) => {
      toast.error(t('nis2:toast.failedToSaveProgress'), { description: error.message });
    },
  });

  const { mutate: completeSession, isPending: isCompleting } = useCompleteOnboarding({
    onSuccess: (response) => {
      toast.success(t('nis2:toast.onboardingCompleted'));
      reset();
      void navigate({ to: response.redirectUrl });
    },
    onError: (error) => {
      const missingFields = (error as ApiClientError & { missingFields?: string[] }).missingFields;
      if (missingFields && missingFields.length > 0) {
        toast.error(t('nis2:toast.cannotCompleteOnboarding'), {
          description: t('onboarding.missingRequiredFields', {
            fields: missingFields.map((field) => fieldLabels.get(field) ?? field).join(', '),
          }),
        });
      } else {
        toast.error(t('nis2:toast.failedToCompleteOnboarding'), { description: error.message });
      }
    },
  });

  useEffect(() => {
    setCurrentModule(module);
  }, [module, setCurrentModule]);

  useEffect(() => {
    if (!urlSessionId && existingSessions?.items[0]) {
      const existingSession = existingSessions.items[0];
      setActiveSession(existingSession.id);
      void navigate({
        to: '/nis2/onboarding',
        search: { sessionId: existingSession.id },
        replace: true,
      });
    }
  }, [existingSessions, urlSessionId, navigate, setActiveSession]);

  useEffect(() => {
    if (session) {
      setActiveSession(session.id);
      setLocalCurrentStep(session.currentStep);
    }
  }, [session, setActiveSession, setLocalCurrentStep]);

  const handleStartOnboarding = useCallback(async () => {
    // Save any selected jurisdictions before starting the AI-driven onboarding
    if (selectedJurisdictions.length > 0 && orgId) {
      for (const countryCode of selectedJurisdictions) {
        const transposition = EU_TRANSPOSITIONS[countryCode];
        try {
          await createJurisdictionMutation.mutateAsync({
            countryCode,
            countryName: transposition?.countryName ?? countryCode,
            isPrimary: selectedJurisdictions.indexOf(countryCode) === 0,
            csirt: {
              name: transposition?.enforcementAuthority ?? null,
              website: null,
              earlyWarningEmail: null,
              notificationEmail: null,
              portalUrl: null,
              emergencyPhone: null,
            },
          });
        } catch {
          // Jurisdiction may already exist — continue
        }
      }
    }
    reset();
    createSession({ module });
  }, [createSession, module, reset, selectedJurisdictions, orgId, createJurisdictionMutation]);

  const localFormData = useOnboardingStore.use.localFormData();
  const latestCompletedSession = completedSessions?.items[0];
  const shouldShowCompletedState =
    (!session && !sessionIdToUse && Boolean(latestCompletedSession)) ||
    session?.status === 'completed';
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const handleStepChange = useCallback(
    (step: number) => {
      if (!session) return;
      setLocalCurrentStep(step);
      // Send both step change and current form data in a single PATCH to avoid
      // race conditions where a stale refetch between two separate PATCHes
      // overwrites local form state.
      updateSession({
        sessionId: session.id,
        data: { currentStep: step, formData: localFormData },
      });
    },
    [session, setLocalCurrentStep, updateSession, localFormData]
  );

  const handleFormDataChange = useCallback(
    (formData: Record<string, unknown>) => {
      if (!session) return;
      updateSession({
        sessionId: session.id,
        data: { formData },
      });
    },
    [session, updateSession]
  );
  const handleComplete = useCallback(() => {
    if (!session) return;
    completeSession({ sessionId: session.id, formData: localFormData });
  }, [session, completeSession, localFormData]);

  if (
    isLoadingSessions ||
    isLoadingCompletedSessions ||
    isFetchingCompletedSessions ||
    (sessionIdToUse && isLoadingSession)
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('onboarding.loading')}</p>
        </div>
      </div>
    );
  }

  if (shouldShowCompletedState) {
    const completedAt = session?.completedAt ?? latestCompletedSession?.lastActivityAt;
    const completedSteps = onboardingConfig.steps.map((step, index) => ({
      step: index + 1,
      label: step.title,
      desc: step.description,
    }));

    return (
      <div className="min-h-screen bg-background p-8" data-testid="onboarding-completed">
        <div className="mx-auto max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">{t('onboarding.completedTitle')}</h1>
            <p className="text-muted-foreground">{t('onboarding.completedDescription')}</p>
            {completedAt ? (
              <p className="text-sm text-muted-foreground">
                {t('onboarding.completedAt', {
                  date: new Date(completedAt).toLocaleString(locale),
                })}
              </p>
            ) : null}
          </div>

          {/* Step overview — always accessible for review */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {completedSteps.map((s) => (
              <div
                key={s.step}
                className="rounded-lg border p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                data-testid={`onboarding-step-${String(s.step)}`}
                onClick={() => {
                  // Re-open the onboarding session at this step
                  void handleStartOnboarding();
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                    {String(s.step)}
                  </span>
                  <p className="text-sm font-medium">{s.label}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              onClick={() => {
                void navigate({ to: '/nis2/dashboard' });
              }}
              data-testid="btn-go-to-dashboard"
            >
              {t('onboarding.goToDashboard')}
            </Button>
            <Button
              variant="outline"
              onClick={handleStartOnboarding}
              disabled={isCreating}
              data-testid="btn-review-onboarding"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('onboarding.starting')}
                </>
              ) : (
                t('onboarding.reviewAndUpdate')
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!session && !sessionIdToUse) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="max-w-2xl text-center space-y-6">
          <h1 className="text-2xl font-bold">{t('onboarding.classificationSetupTitle')}</h1>
          <p className="text-muted-foreground">{t('onboarding.classificationSetupDescription')}</p>

          <NIS2JurisdictionSelect
            selected={selectedJurisdictions}
            onChange={setSelectedJurisdictions}
          />

          <Button
            size="lg"
            onClick={() => void handleStartOnboarding()}
            disabled={isCreating || createJurisdictionMutation.isPending}
          >
            {isCreating || createJurisdictionMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('onboarding.starting')}
              </>
            ) : (
              t('onboarding.startSetup')
            )}
          </Button>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('onboarding.errorLoadingSession')}</AlertTitle>
          <AlertDescription>
            {sessionError instanceof Error
              ? sessionError.message
              : t('onboarding.failedToLoadSession')}
          </AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={handleStartOnboarding}>
          {t('onboarding.startNewSession')}
        </Button>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <div className="h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)]">
      <OnboardingLayout
        module={session.module}
        sessionId={session.id}
        conversationId={session.conversationId ?? ''}
        currentStep={session.currentStep}
        totalSteps={session.totalSteps}
        formData={session.formData}
        onStepChange={handleStepChange}
        onFormDataChange={handleFormDataChange}
        onComplete={handleComplete}
        isLoading={isUpdating || isCompleting}
      />
    </div>
  );
}
